/* Stone Haus pool tile buy box — Sep 29, 2026
   Markup: snippets/sh-pool-buybox.liquid. Styles: sh-cladding-buybox.css + sh-pool-buybox.css.
   Sold per m². After the area is entered, the box shows how it is packed: whole cartons + loose sheets. */
(() => {
  if (window.__splBuyboxBooted) return;
  window.__splBuyboxBooted = true;

  const toNumber = (input) => {
    if (!input) return 0;
    const value = parseFloat(String(input.value || '').replace(',', '.'));
    return Number.isFinite(value) && value > 0 ? value : 0;
  };
  const toFloat = (value) => {
    const n = parseFloat(String(value || '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  // Round to 3dp first so floating point noise (11.000000001) doesn't bump a whole unit.
  const ceilQty = (value) => Math.ceil(Math.round(value * 1000) / 1000);
  const trimNum = (value) => String(Math.round(value * 100) / 100);

  const money = (cents, currency) => {
    const amount = Math.round(cents) / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency || 'AUD',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(amount % 1 === 0 ? 0 : 2);
    }
  };

  const sydneyWeekday = () => {
    try {
      return new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', weekday: 'short' }).format(new Date());
    } catch (error) {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date().getDay()];
    }
  };

  const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);

  // Sheet coverage: carton area ÷ sheets per carton (the supplier's laid coverage, joints included),
  // otherwise the sheet size itself ("310×467" in mm).
  const sheetArea = (root) => {
    const cartonArea = toFloat(root.dataset.cartonArea);
    const perCarton = Math.floor(toFloat(root.dataset.perCarton));
    if (cartonArea && perCarton) return cartonArea / perCarton;
    const dims = String(root.dataset.sheetSize || '').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    if (dims) return (parseFloat(dims[1]) * parseFloat(dims[2])) / 1000000;
    return 0;
  };

  function init(root) {
    if (!root || root.dataset.splReady === 'true') return;
    root.dataset.splReady = 'true';

    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => Array.from(root.querySelectorAll(selector));

    const variantId = Number(root.dataset.variant) || 0;
    const price = Number(root.dataset.price) || 0;
    const available = root.dataset.available === 'true';
    const waste = Number(root.dataset.waste) || 0;
    const afterpayLimit = Number(root.dataset.afterpayLimit) || 4000;
    const currency = root.dataset.currency || 'AUD';
    const title = root.dataset.title || 'this mosaic';
    const perSheet = sheetArea(root);
    const perCarton = Math.floor(toFloat(root.dataset.perCarton));

    const el = {
      tabs: qa('[data-shc-tab]'),
      panels: qa('[data-shc-panel]'),
      area: q('[data-spl-area]'),
      length: q('[data-spl-length]'),
      width: q('[data-spl-width]'),
      depth: q('[data-spl-depth]'),
      poolNote: q('[data-spl-pool-note]'),
      wasteToggle: q('[data-spl-waste-toggle]'),
      pack: q('[data-spl-pack]'),
      cartons: q('[data-spl-cartons]'),
      cartonsLabel: q('[data-spl-cartons-label]'),
      loose: q('[data-spl-loose]'),
      looseLabel: q('[data-spl-loose-label]'),
      sheets: q('[data-spl-sheets]'),
      packNote: q('[data-spl-pack-note]'),
      summary: q('[data-spl-summary]'),
      label: q('[data-spl-label]'),
      qty: q('[data-spl-qty]'),
      cost: q('[data-spl-cost]'),
      total: q('[data-spl-total]'),
      afterpay: q('[data-spl-afterpay]'),
      atc: q('[data-spl-atc]'),
      atcLabel: q('[data-spl-atc-label]'),
      buyNow: q('[data-spl-buynow]'),
      error: q('[data-spl-error]'),
      quote: q('[data-spl-quote]'),
      exampleToggle: q('[data-spl-example-toggle]'),
      ready: q('[data-spl-ready]')
    };

    let mode = 'area';
    let state = { qty: 0, total: 0 };
    const afterpayDefault = el.afterpay ? el.afterpay.innerHTML : '';

    /* ---------- Tabs ---------- */
    const poolArea = () => {
      const l = toNumber(el.length);
      const w = toNumber(el.width);
      const d = toNumber(el.depth);
      if (!l || !w) return 0;
      return l * w + 2 * (l + w) * d;
    };

    const setMode = (next, focus) => {
      if (next === mode) return;
      // Carry the measurement across so switching tabs never loses work.
      if (next === 'area' && el.area && !toNumber(el.area)) {
        const sized = poolArea();
        if (sized > 0) el.area.value = trimNum(sized);
      }
      mode = next;
      el.tabs.forEach((tab) => {
        const active = tab.dataset.shcTab === mode;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      el.panels.forEach((panel) => { panel.hidden = panel.dataset.shcPanel !== mode; });
      update();
    };

    el.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setMode(tab.dataset.shcTab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const nextIndex = (index + (event.key === 'ArrowRight' ? 1 : -1) + el.tabs.length) % el.tabs.length;
        setMode(el.tabs[nextIndex].dataset.shcTab, true);
      });
    });

    /* ---------- Calculation ---------- */
    const calculate = () => {
      const area = mode === 'area' ? toNumber(el.area) : poolArea();
      const wasteOn = !!(el.wasteToggle && el.wasteToggle.checked && waste > 0);
      const qty = available && area > 0 ? ceilQty(area * (wasteOn ? 1 + waste / 100 : 1)) : 0;

      let sheets = 0;
      let cartons = 0;
      let loose = 0;
      if (qty && perSheet) {
        sheets = ceilQty(qty / perSheet);
        if (perCarton) {
          cartons = Math.floor(sheets / perCarton);
          loose = sheets - cartons * perCarton;
        }
      }
      return { area, wasteOn, qty, sheets, cartons, loose, packed: sheets * perSheet, total: qty * price };
    };

    const packText = (r) => {
      if (!r.sheets) return '';
      if (!perCarton) return plural(r.sheets, 'sheet', 'sheets');
      const parts = [];
      if (r.cartons) parts.push(plural(r.cartons, 'carton', 'cartons'));
      if (r.loose) parts.push(plural(r.loose, 'sheet', 'sheets'));
      return parts.join(' + ');
    };

    const permalink = () => {
      if (!state.qty || !variantId) return '#';
      const base0 = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const base = base0.endsWith('/') ? base0 : base0 + '/';
      const shopPay = el.buyNow && el.buyNow.dataset.shopPay === 'true';
      return base + 'cart/' + variantId + ':' + state.qty + (shopPay ? '?payment=shop_pay' : '');
    };

    const update = () => {
      const r = calculate();
      state = r;
      const hasQty = r.qty > 0;

      if (el.poolNote) {
        const l = toNumber(el.length);
        const w = toNumber(el.width);
        const d = toNumber(el.depth);
        el.poolNote.hidden = !(mode === 'size' && l && w);
        el.poolNote.textContent = l && w
          ? 'Floor ' + trimNum(l) + ' × ' + trimNum(w) + (d ? ' + walls 2 × (' + trimNum(l) + ' + ' + trimNum(w) + ') × ' + trimNum(d) : '') + ' = ' + trimNum(poolArea()) + ' m²'
          : '';
      }

      if (el.pack) {
        el.pack.hidden = !(hasQty && r.sheets);
        if (el.cartons) el.cartons.textContent = String(r.cartons);
        if (el.cartonsLabel) el.cartonsLabel.textContent = r.cartons === 1 ? 'carton' : 'cartons';
        if (el.loose) el.loose.textContent = String(r.loose);
        if (el.looseLabel) el.looseLabel.textContent = r.loose === 1 ? 'loose sheet' : 'loose sheets';
        if (el.sheets) el.sheets.textContent = String(r.sheets);
        if (el.packNote && r.sheets) {
          el.packNote.textContent = plural(r.sheets, 'sheet', 'sheets') + ' cover about ' + trimNum(r.packed) + ' m², enough for the ' + r.qty + ' m² you are ordering.';
        }
      }

      if (el.summary) el.summary.hidden = !hasQty;
      if (el.label) {
        el.label.textContent = 'Mosaic · ' + trimNum(r.area) + ' m²' + (r.wasteOn ? ' + ' + waste + '%' : '') + ', rounded up';
      }
      if (el.qty) el.qty.textContent = r.qty + ' m²';
      if (el.cost) el.cost.textContent = money(r.total, currency);
      if (el.total) el.total.textContent = money(r.total, currency);

      if (el.afterpay) {
        if (!hasQty) {
          el.afterpay.innerHTML = afterpayDefault;
        } else if (r.total / 100 <= afterpayLimit) {
          el.afterpay.innerHTML = 'or 4 payments of <strong>' + money(Math.round(r.total / 4), currency) +
            '</strong> for this order <span class="shc-dot">·</span> up to ' + money(afterpayLimit * 100, currency);
        } else {
          el.afterpay.innerHTML = 'Afterpay is available on orders up to ' + money(afterpayLimit * 100, currency);
        }
      }

      if (el.atc) {
        el.atc.disabled = !hasQty;
        if (!available) {
          el.atcLabel.textContent = 'Out of stock';
        } else if (!hasQty) {
          el.atcLabel.textContent = el.atc.dataset.defaultLabel || 'Enter your area to see your total';
        } else {
          el.atcLabel.textContent = 'Add ' + r.qty + ' m² to cart · ' + money(r.total, currency);
        }
      }

      if (el.buyNow) {
        el.buyNow.href = permalink();
        el.buyNow.classList.toggle('is-disabled', !hasQty);
        el.buyNow.setAttribute('aria-disabled', String(!hasQty));
      }
      if (el.error) el.error.hidden = true;
    };

    root.addEventListener('input', (event) => {
      if (event.target.matches('input[type="number"]')) update();
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-spl-waste-toggle]')) update();
    });

    /* ---------- Add to cart ---------- */
    const showError = (message) => {
      if (!el.error) return;
      el.error.textContent = message || 'Something went wrong. Please try again or call us.';
      el.error.hidden = false;
    };

    const addToCart = async () => {
      const r = calculate();
      if (!r.qty || !variantId) return;
      const item = { id: variantId, quantity: r.qty };
      const packed = packText(r);
      if (packed) item.properties = { 'Packed as': packed };

      const drawer = document.querySelector('cart-drawer');
      const useDrawer = !!(drawer && drawer.dataset.cartType === 'drawer' && typeof drawer.renderContents === 'function');
      const sections = useDrawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id)
        : [];

      el.atc.disabled = true;
      el.atc.classList.add('is-loading');
      const previousLabel = el.atcLabel.textContent;
      el.atcLabel.textContent = 'Adding…';

      try {
        const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const base = rootPath.endsWith('/') ? rootPath : rootPath + '/';
        const body = { items: [item] };
        if (sections.length) {
          body.sections = sections.join(',');
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(base + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'We could not add that to your cart.');
        }

        try {
          // eslint-disable-next-line no-undef
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'spl-buybox', cartData: result });
        } catch (error) {}

        if (useDrawer && result.sections) {
          try {
            drawer.classList.remove('is-empty');
            drawer.renderContents(result, !document.body.classList.contains('page-overlay-cart-on'));
          } catch (error) {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }
        el.atcLabel.textContent = 'Added to cart ✓';
        setTimeout(update, 1800);
      } catch (error) {
        el.atcLabel.textContent = previousLabel;
        showError(error.message);
        el.atc.disabled = false;
      } finally {
        el.atc.classList.remove('is-loading');
      }
    };

    if (el.atc) el.atc.addEventListener('click', addToCart);
    if (el.buyNow) {
      el.buyNow.addEventListener('click', (event) => {
        if (el.buyNow.classList.contains('is-disabled')) event.preventDefault();
      });
    }

    /* ---------- Enquiry panel (quote) ---------- */
    const openEnquiry = (message) => {
      const panel = document.querySelector('[id*="stonehaus_enquiry_panel"]');
      if (!panel) {
        window.location.href = '/pages/contact';
        return;
      }
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const box = panel.querySelector('textarea');
      if (!box) return;
      if (!box.value.trim() || box.dataset.splFilled === 'true') {
        box.value = message;
        box.dataset.splFilled = 'true';
      }
      setTimeout(() => { try { box.focus({ preventScroll: true }); } catch (error) {} }, 500);
    };

    if (el.quote) {
      el.quote.addEventListener('click', (event) => {
        event.preventDefault();
        const r = calculate();
        const lines = ['Quote request: ' + title];
        if (mode === 'size' && toNumber(el.length) && toNumber(el.width)) {
          lines.push('Pool: ' + trimNum(toNumber(el.length)) + ' × ' + trimNum(toNumber(el.width)) + ' m, average depth ' + trimNum(toNumber(el.depth)) + ' m');
        }
        if (r.area) lines.push('Approx. area: ' + trimNum(r.area) + ' m²');
        if (r.sheets) lines.push('Packed as: ' + packText(r));
        const postcode = root.querySelector('[data-sh-delivery-postcode]');
        if (postcode && postcode.value.trim()) lines.push('Delivery postcode: ' + postcode.value.trim());
        openEnquiry(lines.join('\n') + '\n');
      });
    }

    // Pool tile wording for the shared enquiry panel (its template copy is written for cladding).
    if (root.dataset.enquiryText) {
      document.querySelectorAll('[id*="stonehaus_enquiry_panel"] .sh-enq__text').forEach((node) => {
        const p = document.createElement('p');
        p.textContent = root.dataset.enquiryText;
        node.replaceChildren(p);
      });
    }
    if (root.dataset.enquiryPlaceholder) {
      document.querySelectorAll('[id*="stonehaus_enquiry_panel"] textarea').forEach((node) => {
        node.setAttribute('placeholder', root.dataset.enquiryPlaceholder);
      });
    }

    /* ---------- Worked example ---------- */
    if (el.exampleToggle) {
      el.exampleToggle.addEventListener('click', () => {
        const panel = document.getElementById(el.exampleToggle.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !panel.hidden;
        el.exampleToggle.setAttribute('aria-expanded', String(!panel.hidden));
      });
    }

    /* ---------- Fulfilment options ---------- */
    const options = qa('[data-spl-opt]');
    const syncOptions = () => {
      options.forEach((option) => {
        const radio = option.querySelector('input[type="radio"]');
        option.classList.toggle('is-active', !!(radio && radio.checked));
      });
    };
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-spl-opt] input[type="radio"]')) syncOptions();
    });
    options.forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');
      option.addEventListener('focusin', (event) => {
        if (radio && !radio.checked && event.target !== radio && option.contains(event.target)) {
          radio.checked = true;
          syncOptions();
        }
      });
      option.addEventListener('click', (event) => {
        if (radio && !radio.checked && !event.target.closest('a, button, input')) {
          radio.checked = true;
          syncOptions();
        }
      });
    });

    const estimator = q('.sh-delivery-estimator');
    if (estimator) {
      const button = estimator.querySelector('[data-sh-delivery-submit]');
      const input = estimator.querySelector('[data-sh-delivery-postcode]');
      if (button) button.textContent = 'Check';
      if (input) input.setAttribute('placeholder', 'Postcode, e.g. 2170');
    }

    if (el.ready) {
      el.ready.textContent = sydneyWeekday() === 'Sat' ? 'Monday' : 'Tomorrow';
    }

    update();
  }

  const scan = (scope) => {
    (scope || document).querySelectorAll('[data-spl-buybox]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
